import { Location } from '@angular/common';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import type { ActivatedRoute, Params, Router, UrlTree } from '@angular/router';
import type { Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { MediaElementsService } from 'src/services/media-elements/media-elements.service';
import { StickyService } from 'src/services/sticky/sticky.service';
import { ENVIRONMENT_ORIGIN } from 'src/tokens/environment-origin/environment-origin';

@Injectable({
    providedIn: 'root',
})
export class AnchorsService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly location = inject(Location);
    private readonly envOrigin = inject(ENVIRONMENT_ORIGIN);
    private readonly widgetsService = inject(WidgetsService);
    private readonly mediaElementsService = inject(MediaElementsService);
    private readonly stickyService = inject(StickyService);

    private router: Router;
    private route: ActivatedRoute;
    private fragment$$ = new BehaviorSubject<string>('');
    public readonly fragment$ = this.fragment$$.asObservable();

    private shouldScrollToElement = true;

    public initialize(router: Router, route: ActivatedRoute): Subscription {
        this.router = router;
        this.route = route;

        this.subscribeToRouteFragmentChange();

        return this.fragment$$.pipe(filter((fragment) => !!fragment)).subscribe(this.handleFragmentChange.bind(this));
    }

    public handleAnchorClick(event): void {
        let anchorElement: HTMLAnchorElement;

        if (event?.target instanceof HTMLAnchorElement) {
            anchorElement = event.target;
        } else if (event?.target?.parentElement instanceof HTMLAnchorElement) {
            anchorElement = event?.target?.parentElement;
        }

        if (!anchorElement?.hash) {
            return;
        }

        if (this.isAnchorTargetOnTheSamePage(anchorElement)) {
            const fragment: string = anchorElement.hash.replace('#', '');
            const currentUrlTree: UrlTree = this.router.parseUrl(this.router.url);
            const queryParams: Params = currentUrlTree.queryParams;
            const fragmentIsUnchanged = currentUrlTree.fragment === fragment;

            currentUrlTree.fragment = null;
            currentUrlTree.queryParams = {};

            event.preventDefault();

            this.router.navigate([currentUrlTree.toString()], { fragment, queryParams }).then(() => {
                if (fragmentIsUnchanged) {
                    this.fragment$$.next(fragment);
                }
            });
        }
    }

    public clickOnAnchor(url: string) {
        const currentPath = this.location.path();
        const newUrl = new URL(currentPath, this.envOrigin);
        const normalizedPath = this.location.normalize(newUrl.pathname);
        newUrl.hash = url;

        this.location.replaceState(`${normalizedPath}/${newUrl.search}${newUrl.hash}`);
        this.fragment$$.next(url);
        return this.scrollToElement(url);
    }

    private subscribeToRouteFragmentChange(): Subscription {
        return this.route.fragment.pipe(filter((fragment) => !!fragment)).subscribe((fragment: string) => {
            this.fragment$$.next(fragment);
        });
    }

    private handleFragmentChange(fragment: string): void {
        this.shouldScrollToElement = true;
        const handleUserScrollBind = this.handleUserScroll.bind(this);

        this.widgetsService
            .waitForWidgets()
            .then(() => this.mediaElementsService.waitForMediaElements())
            .then(() => {
                this.document.addEventListener('scroll', handleUserScrollBind, { once: true });
            })
            .then(() => {
                if (this.shouldScrollToElement) {
                    return this.scrollToElement(fragment);
                } else {
                    return Promise.resolve(false);
                }
            })
            .then(() => {
                this.document.removeEventListener('scroll', handleUserScrollBind);
            });
    }

    private handleUserScroll(): void {
        this.shouldScrollToElement = false;
    }

    private scrollToElement(id: string): Promise<boolean> {
        const selector = this.createSelector(id);
        const element = this.document.querySelector(selector) as HTMLElement;

        if (!element) return Promise.resolve(false);

        const totalMargin = this.stickyService.getPredictedOffset(element) + 20;
        const elementTopOffset = element.getBoundingClientRect().top;

        window.scrollBy({
            top: elementTopOffset - totalMargin,
            behavior: 'smooth',
        });

        return Promise.resolve(true);
    }

    private isAnchorTargetOnTheSamePage(anchorElement: HTMLAnchorElement): boolean {
        const fullPath = window.location.origin + window.location.pathname + anchorElement.hash;
        const path = window.location.origin + '/' + anchorElement.hash;
        return anchorElement.href === fullPath || anchorElement.href === path;
    }

    private createSelector(anchorId: string): string {
        const idWithoutHash = anchorId.replace('#', '');
        return `[anchor-id="${idWithoutHash}"]`;
    }
}
